// frontend/src/components/production-sequence/CommentHoverCard.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const CARD_W = 340;    // ancho de la tarjeta (px)
const CARD_MAX_H = 260;
const CLOSE_MS = 120;  // margen para llevar el ratón de la celda a la tarjeta

/**
 * Comentario de una OT. En la celda va recortado a una línea, con un marcador que
 * delata de un vistazo qué órdenes llevan comentario; al pasar el ratón —o al
 * enfocar con el teclado— se abre una tarjeta con el texto completo, respetando los
 * saltos de línea.
 *
 * Sustituye al atributo `title` nativo, que tardaba ~1 s en aparecer, no se podía
 * seleccionar ni estilar y se perdía al mover el ratón. Un clic FIJA la tarjeta para
 * poder copiar el texto; se cierra con Escape, con la × o pinchando fuera.
 *
 * Se renderiza en portal para escapar del `overflow` y del stacking de la tabla, y
 * se cierra al hacer scroll porque su posición es fija — mismo criterio que
 * ColumnFilterPopover.
 */
export function CommentHoverCard({ text, dark }: { text: string; dark: boolean }) {
  const { t } = useTranslation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [pinned, setPinned] = useState(false);

  const open = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    if (btnRef.current) setAnchor(btnRef.current.getBoundingClientRect());
  }, []);

  const close = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setPinned(false);
    setAnchor(null);
  }, []);

  // Cierre con retardo: la celda y la tarjeta están separadas 4px, y sin margen se
  // cerraría en el trayecto del ratón. Fijada, salir con el ratón no la cierra.
  const scheduleClose = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      if (!pinned) setAnchor(null);
    }, CLOSE_MS);
  }, [pinned]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!anchor) return;
    // El scroll PROPIO de la tarjeta (comentarios largos) no debe cerrarla: el
    // listener va en captura, así que también le llegan los scrolls de dentro.
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && cardRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [anchor, close]);

  const card = dark
    ? 'bg-slate-900 border-white/10 text-slate-200'
    : 'bg-white border-slate-200 text-slate-700';

  // Posición fija anclada a la celda, sin salirse por la derecha. Si abajo no cabe
  // y arriba sí, la tarjeta crece hacia arriba.
  let placement: { top: number } | { bottom: number } = { top: 0 };
  let left = 0;
  if (anchor) {
    left = Math.max(8, Math.min(anchor.left, window.innerWidth - CARD_W - 8));
    const fitsBelow = window.innerHeight - anchor.bottom > 200;
    placement = fitsBelow || anchor.top < 220
      ? { top: anchor.bottom + 4 }
      : { bottom: window.innerHeight - anchor.top + 4 };
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        // stopPropagation: la fila entera es clicable (selecciona la OT) y abrir el
        // comentario no debe cambiar la selección.
        onClick={e => { e.stopPropagation(); setPinned(p => !p); open(); }}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        className="flex items-center gap-1 w-full min-w-0 text-left cursor-pointer"
        aria-label={t('sequence.comment.view')}
      >
        <svg
          className={`w-3 h-3 shrink-0 ${pinned ? 'text-sky-400' : 'text-sky-500'}`}
          viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"
        >
          <path d="M2 1.5h8A1.5 1.5 0 0 1 11.5 3v4A1.5 1.5 0 0 1 10 8.5H5.7l-2.5 2.1a.5.5 0 0 1-.8-.4V8.5H2A1.5 1.5 0 0 1 .5 7V3A1.5 1.5 0 0 1 2 1.5Z" />
        </svg>
        <span className="min-w-0 truncate">{text}</span>
      </button>

      {anchor && createPortal(
        <>
          {/* Fondo de cierre SÓLO estando fijada: permanente bloquearía el hover del
              resto de la tabla. */}
          {pinned && (
            <div className="fixed inset-0 z-[60]" onMouseDown={close} aria-hidden="true" />
          )}
          <div
            ref={cardRef}
            role={pinned ? 'dialog' : 'tooltip'}
            className={`fixed z-[61] rounded-xl border shadow-2xl px-3 py-2.5 text-[12px] leading-snug whitespace-pre-wrap break-words select-text ${card}`}
            style={{ left, width: CARD_W, maxHeight: CARD_MAX_H, overflowY: 'auto', ...placement }}
            onMouseEnter={open}
            onMouseLeave={scheduleClose}
            onClick={e => e.stopPropagation()}
          >
            {pinned && (
              <button
                type="button"
                onClick={close}
                className={`float-right -mt-0.5 ml-2 w-5 h-5 flex items-center justify-center rounded cursor-pointer ${
                  dark ? 'text-slate-400 hover:bg-white/10 hover:text-slate-100'
                       : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                }`}
                aria-label={t('sequence.comment.close')}
              >
                <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                  <path d="M1 1l8 8M9 1L1 9" />
                </svg>
              </button>
            )}
            {text}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
